import React, { useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  TopToolbar,
  FilterButton,
  ExportButton,
  TextInput,
  SelectInput,
} from "react-admin";
import { Chip, Button, Box } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VerifyEditDialog from "./verify-truck-dialog";
import { FunctionField } from "react-admin";
import { getTruckPlateColorLabel, TruckVerifyInfo } from "../../models/Truck";

// 认证状态选项
const verifyStatusOptions = [
  { id: 0, name: "待审核" },
  { id: 1, name: "已通过" },
  { id: -1, name: "未通过" },
];

// 修改状态显示组件
const StatusField = ({ record }: { record: TruckVerifyInfo }) => {
  const status = record.status;
  const getStatusColor = (status: number) => {
    switch (status) {
      case 0:
        return "warning";
      case 1:
        return "success";
      case -1:
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status: number) => {
    const option = verifyStatusOptions.find((opt) => opt.id === status);
    return option ? option.name : "未知状态";
  };

  return (
    <Chip
      label={getStatusText(status)}
      color={getStatusColor(status)}
      size="small"
    />
  );
};

// 筛选器
const VerifyListFilters = [
  <TextInput source="name" label="姓名" />,
  <TextInput source="plate" label="车牌号" alwaysOn />,
  <TextInput source="vin" label="VIN码" />,
  <SelectInput
    source="status"
    label="认证状态"
    choices={verifyStatusOptions}
  />,
];

// 工具栏
const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

// 修改 ActionButtons 的类型定义
const ActionButtons = ({
  record,
  onView,
}: {
  record?: TruckVerifyInfo;
  onView: (record: TruckVerifyInfo) => void;
}) => {
  if (!record) return null;

  return (
    <Box>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          onView(record);
        }}
        startIcon={<VisibilityIcon />}
        size="small"
        sx={{ mr: 1 }}
      >
        状态变更
      </Button>
    </Box>
  );
};

const VerifyTrucksList = () => {
  const [selectedVerify, setSelectedVerify] = useState<TruckVerifyInfo | null>(
    null,
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpenDialog = (record: any) => {
    console.log("handleOpenDialog", record);
    setSelectedVerify(record);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedVerify(null);
  };

  return (
    <>
      <List
        actions={<ListActions />}
        filters={VerifyListFilters}
        sort={{ field: "createdAt", order: "DESC" }}
        perPage={10}
      >
        <Datagrid bulkActionButtons={false}>
          <TextField source="plate" label="车牌号" />
          <TextField source="vin" label="VIN码" />
          <TextField source="truck.type" label="车长/车型" />
          <FunctionField
            label="车牌颜色"
            render={(record: TruckVerifyInfo) =>
              getTruckPlateColorLabel(record.truck?.plate_color)
            }
          />
          <FunctionField
            label="认证状态"
            render={(record: TruckVerifyInfo) => (
              <Chip
                label={record.truck?.verified ? "已认证" : "未认证"}
                color={record.truck?.verified ? "success" : "default"}
                size="small"
              />
            )}
          />
          <TextField source="remark" label="备注" />
          <DateField source="createdAt" label="申请时间" showTime />
          <DateField source="updatedAt" label="更新时间" showTime />
          <FunctionField<TruckVerifyInfo>
            label="操作"
            render={(record) => (
              <ActionButtons record={record} onView={handleOpenDialog} />
            )}
          />
        </Datagrid>
      </List>

      <VerifyEditDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        verifyId={selectedVerify?.id ?? ""}
        verifyData={selectedVerify}
      />
    </>
  );
};

export default VerifyTrucksList;
